import { AllNamedConfigTypeCases } from '../WidgetTypeSelect';
import { BaseWidgetFormFields } from 'components/consumer-page-widgets/BaseWidgetFormFields';
import { Button, FormSelectField, IconButton, TextField } from 'ui';
import { FieldArray, useFormikContext } from 'formik';
import { Grid } from '@mui/material';
import { consumer } from '@tock/coretypes';
import { tocktix } from '@tock/protos/dist/types/clowncar';
import { useEffect, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import DeleteIcon from '@mui/icons-material/Delete';

export const invalidMetroWidgetItemTypes: AllNamedConfigTypeCases[] = ['metroAreaWidgetConfig'];

interface MetroAreaContainerWidgetFormFieldsProps {
  marketingWidgets: consumer.NamedConfiguration[];
  metroAreas: consumer.MetroArea[];
  setFieldValue(
    field: string,
    value: consumer.IMetroAreaContainerWidgetItemConfig[] | number
  ): void;
}

export interface MetroAreaContainerWidgetFields extends BaseWidgetFormFields {
  defaultWidgetNamedConfigurationId?: number | null;
  widgets: consumer.IMetroAreaContainerWidgetItemConfig[];
}

export const MetroAreaContainerWidgetFormFields: React.FunctionComponent<
  React.PropsWithChildren<MetroAreaContainerWidgetFormFieldsProps>
> = (props: MetroAreaContainerWidgetFormFieldsProps) => {
  const { marketingWidgets, metroAreas, setFieldValue } = props;

  const [defaultWidgetNamedConfigurationId, setDefaultWidgetNamedConfigurationId] = useState(-1);
  const [widgetMetros, setWidgetMetros] = useState<tocktix.proto.clowncar.IMetroWidgetConfig[]>([]);

  const { values } = useFormikContext<MetroAreaContainerWidgetFields>();
  useEffect(() => {
    setWidgetMetros(values.widgets);
    setDefaultWidgetNamedConfigurationId(values.defaultWidgetNamedConfigurationId || -1);
  }, [values]);

  const defaultOption = <option key={-1} label={''} value={-1} />;

  const marketingWidgetConfigurationOptions = marketingWidgets
    .filter((config) => {
      return config.widgetType === 'widget-creator-widget';
    })
    .sort((first, second) => {
      return first.name.localeCompare(second.name);
    })
    .map((config) => {
      return <option key={config.id} label={config.name} value={config.id} />;
    });

  marketingWidgetConfigurationOptions.unshift(defaultOption);
  const metroAreaOptions = metroAreas.sort((a, b) => {
    if (a.state?.toUpperCase() === b.state?.toUpperCase()) {
      return a.name?.toUpperCase().localeCompare(b.name?.toUpperCase());
    }
    return a.state?.toUpperCase().localeCompare(b.state?.toUpperCase());
  });

  return (
    <>
      <FormSelectField
        id="defaultWidgetNamedConfigurationId"
        name="defaultWidgetNamedConfigurationId"
        label="Default Widget"
        value={`${defaultWidgetNamedConfigurationId}`}
        fullWidth
      >
        {marketingWidgetConfigurationOptions}
      </FormSelectField>
      <FieldArray
        name="widgets"
        render={(arrayHelpers) => (
          <div>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <h4>Widget</h4>
              </Grid>
              <Grid item xs={4}>
                <h4>Metro Area</h4>
              </Grid>
              <Grid item xs={4}></Grid>
            </Grid>
            {widgetMetros.map((widgetMetro, index) => {
              // Explicitly allowing any because this value is a MetroArea[], but setFieldValue expects IMetroAreaContainerWidgetItemConfig[]
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const onAutocompleteChange = (event: React.ChangeEvent<any>, value: any[]) =>
                setFieldValue(`widgets.${index}.metro`, value);

              return (
                <Grid container spacing={2} key={index}>
                  <Grid item xs={4}>
                    <FormSelectField fullWidth name={`widgets.${index}.namedWidgetConfigurationId`}>
                      {marketingWidgetConfigurationOptions}
                    </FormSelectField>
                  </Grid>
                  <Grid item xs={4}>
                    <Autocomplete
                      multiple={true}
                      fullWidth={true}
                      id={`widgets.${index}.metro`}
                      options={metroAreaOptions}
                      groupBy={(option: consumer.MetroArea) => option.state?.toUpperCase()}
                      getOptionLabel={(option: consumer.MetroArea) => option.name}
                      onChange={onAutocompleteChange}
                      renderInput={(params) => <TextField {...params} label="Metro Areas" />}
                      value={widgetMetro.metro || []}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <IconButton
                      aria-label="Delete row"
                      variant="outlined"
                      onClick={() => {
                        arrayHelpers.remove(index);
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              );
            })}
            <Button
              onClick={() =>
                arrayHelpers.insert(widgetMetros.length, {
                  metro: [],
                  namedWidgetConfigurationId: -1,
                })
              }
            >
              Add a row
            </Button>
          </div>
        )}
      />
    </>
  );
};
